import { useQueryClient } from '@tanstack/react-query';
import { createContext, use, useCallback, useEffect, useMemo, useState } from 'react';
import type { PapelUsuaria } from '@reducee/contracts';

import { chamarApi, ErroDaApi } from '../api/cliente.ts';

export type UsuariaDaSessao = {
  id: string;
  nome: string;
  email: string;
  papel: PapelUsuaria;
};

type EstadoSessao = {
  usuaria: UsuariaDaSessao | null;
  carregando: boolean;
  entrar: (email: string, senha: string) => Promise<void>;
  sair: () => Promise<void>;
  /**
   * Relê quem está logada. Meu perfil chama depois de trocar o nome: a API já
   * reemitiu o token com o nome novo, e o menu precisa acompanhar.
   */
  recarregar: () => Promise<void>;
};

const ContextoSessao = createContext<EstadoSessao | null>(null);

export function ProvedorDeSessao({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [usuaria, setUsuaria] = useState<UsuariaDaSessao | null>(null);
  const [carregando, setCarregando] = useState(true);
  const clienteDeConsultas = useQueryClient();

  useEffect(() => {
    // Recupera a sessão de um cookie ainda válido, para que atualizar a página
    // não obrigue a entrar de novo.
    chamarApi<UsuariaDaSessao>('/autenticacao/eu')
      .then(setUsuaria)
      .catch(() => setUsuaria(null))
      .finally(() => setCarregando(false));
  }, []);

  const entrar = useCallback(async (email: string, senha: string) => {
    const autenticada = await chamarApi<UsuariaDaSessao>('/autenticacao/entrar', {
      metodo: 'POST',
      corpo: { email, senha },
      semRenovar: true,
    });

    /**
     * O cache de consultas vive na aba, não na sessão: o `QueryClient` nasce
     * no escopo do módulo e sobrevive a entrar e sair. Sem limpar aqui, quem
     * entra herda o que a anterior carregou — e o TanStack Query pinta o dado
     * velho antes de revalidar, então a Comercial chegava a ver a lista da
     * Administradora por um instante.
     *
     * Limpa nas duas pontas de propósito. Sair sozinho não bastaria: fechar a
     * sessão pela expiração do cookie não passa por `sair`.
     */
    clienteDeConsultas.clear();
    setUsuaria(autenticada);
  }, [clienteDeConsultas]);

  const sair = useCallback(async () => {
    await chamarApi<void>('/autenticacao/sair', { metodo: 'POST', semRenovar: true }).catch(
      (erro: unknown) => {
        if (!(erro instanceof ErroDaApi)) throw erro;
      },
    );
    clienteDeConsultas.clear();
    setUsuaria(null);
  }, [clienteDeConsultas]);

  const recarregar = useCallback(async () => {
    setUsuaria(await chamarApi<UsuariaDaSessao>('/autenticacao/eu'));
  }, []);

  const valor = useMemo<EstadoSessao>(
    () => ({ usuaria, carregando, entrar, sair, recarregar }),
    [usuaria, carregando, entrar, sair, recarregar],
  );

  return <ContextoSessao value={valor}>{children}</ContextoSessao>;
}

export function useSessao(): EstadoSessao {
  const contexto = use(ContextoSessao);
  if (!contexto) throw new Error('useSessao precisa estar dentro de ProvedorDeSessao.');
  return contexto;
}

export const ROTULO_PAPEL: Record<PapelUsuaria, string> = {
  ADMINISTRADORA: 'Administradora',
  COMERCIAL: 'Comercial',
};
